import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../../../../AppStores";
import type {IPopupWindowConfig} from "../../../../../../modules/abstract/popups/PopupWindow";
import {Report} from "../../../../../../../data/models/Report";
import type {ExportFormat} from "../../../../../../../data/exporters/BaseExporter";
import {PromptWindowV5} from "../../../../../popup/PromptWindowV5";
import {XyiconFeature, FieldDataType, Permission} from "../../../../../../../generated/api/base";
import type {AppState} from "../../../../../../../data/state/AppState";
import type {TransportLayer} from "../../../../../../../data/TransportLayer";
import {SearchFieldV5} from "../../../../../input/search/SearchFieldV5";
import {IconButtonV5} from "../../../../../interaction/IconButtonV5";
import PenWithLineIcon from "../../../../../icons/pen-with-line.svg?react";
import DonwloadIcon from "../../../../../icons/download.svg?react";
import BackIcon from "../../../../../icons/arrow-back.svg?react";
import InfoIcon from "../../../../../icons/circle-information.svg?react";
import ArchiveIcon from "../../../../../icons/history.svg?react";
import PlayIcon from "../../../../../icons/play.svg?react";
import DotsHorizontalIcon from "../../../../../icons/dots-horizontal.svg?react";
import ShareIcon from "../../../../../icons/share.svg?react";
import ChevronDownIcon from "../../../../../icons/chevron-down.svg?react";
import type {IHeader} from "../../../../../abstract/table/SimpleTableV5";
import {SimpleTableV5} from "../../../../../abstract/table/SimpleTableV5";
import {DropdownButtonV5} from "../../../../../interaction/DropdownButtonV5";
import {ButtonV5} from "../../../../../button/ButtonV5";
import {Functions} from "../../../../../../../utils/function/Functions";
import {DomPortal} from "../../../../../../modules/abstract/portal/DomPortal";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../../../utils/dom/DomUtils";
import {PopupV5} from "../../../../../popup/PopupV5";
import {ReportHistoryV5} from "../../../ReportHistoryV5";
import {PopupUtilsV5} from "../../../../../popup/PopupUtilsV5";
import {WarningWindowV5} from "../../../../../popup/WarningWindowV5";
import {ReportScope} from "../../../../../../../generated/api/reports";
import {DateFormatter} from "../../../../../../../utils/format/DateFormatter";
import type {IDropdownOption} from "../../../../../interaction/DropdownOptionsV5";
import type {ViewActionType} from "../../../../../sharing/GlobalSharingPopup";
import {GlobalSharingPopup} from "../../../../../sharing/GlobalSharingPopup";
import {ObjectUtils} from "../../../../../../../utils/data/ObjectUtils";
import {Debouncer} from "../../../../../../../utils/function/Debouncer";
import type {IViewSort} from "../../../../../../../data/models/ViewUtils";
import ReportSharingContainer from "../../../../../sharing/ReportSharingContainer";
import {DropDownTitleStyle, ReportPopupStyled, RunReportPageHeaderNameStyled, RunReportStyled, RunReportTableStyled} from "./RunReportPage.style";
import {RunReportDetailsV5} from "./RunReportDetailsV5";

interface IRunReportPageProps {
	readonly report: Report;
	readonly onClose?: () => void;
	readonly onEditClick?: (report: Report) => void;
	readonly onDuplicateClick?: (reports: Report[]) => void;
	readonly canDeleteReports?: (selectedItems: Report[]) => boolean;
	readonly onRunReport?: (report: Report) => void;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
	readonly isNavbarDocked?: boolean;
	readonly lastRunTime?: number;
	readonly style?: React.CSSProperties;
}

interface IRunReportPageState {
	search: string;
	isDetailsPopupOpen: boolean;
	isSharePopupOpen: boolean;
	isArchivePopupOpen: boolean;
	isGlobalActionPanelOpen: boolean;
	viewAction: ViewActionType;
}

@withAppStores("appState", "transport")
@observer
export class RunReportPageV5 extends React.Component<IRunReportPageProps, IRunReportPageState> {
	private _report: Report;

	private _appState: AppState;

	private readonly _table = React.createRef<SimpleTableV5<string[]>>();
	private readonly _archiveReportButtonRef = React.createRef<HTMLDivElement>();
	private readonly _saveTableSettingsDebouncer = new Debouncer(1000);

	constructor(props: IRunReportPageProps) {
		super(props);
		this._report = props.report;
		this._appState = props.appState;

		this.state = {
			search: "",
			isDetailsPopupOpen: false,
			isSharePopupOpen: false,
			isArchivePopupOpen: false,
			isGlobalActionPanelOpen: false,
			viewAction: "modify",
		};
	}

	private getHeaders = (): IHeader[] => {
		const data = this.props.report.reportData;

		if (data.length > 0) {
			const reportHeader = data[0];

			return reportHeader.map((title: string, index: number) => {
				return {
					id: String(index),
					title: title,
				};
			});
		} else {
			return [];
		}
	};

	private readonly getDefaultSort = (): IViewSort | undefined => {
		const savedSort = this.props.report.tableSettings?.sort;

		if (!savedSort) {
			return undefined;
		}

		const index = this.getHeaders().findIndex((header) => header.title === savedSort.columnTitle);

		return index > -1 ? {column: String(index), direction: savedSort.direction} : undefined;
	};

	private readonly onTableSettingsChange = (headerWidths: {[title: string]: number}, sort: IViewSort) => {
		const {report, appState, transport} = this.props;
		const sortedHeader = sort.direction ? this.getHeaders().find((header) => header.id === sort.column) : null;

		report.setTableSettings({
			columnWidths: headerWidths,
			sort: sortedHeader ? {columnTitle: sortedHeader.title, direction: sort.direction} : null,
		});

		if (Report.getPermission(report, appState) >= Permission.Update) {
			this._saveTableSettingsDebouncer.debounce(() => transport.services.feature.updateReport(report));
		}
	};

	private readonly getFields = (row: string[]) => {
		return [...this._report.displayedFields, ...this._report.displayedLinkedFields]
			.map((fieldData, index) => {
				if (index < row?.length) {
					const field = this._appState.actions.getFieldByRefId(fieldData.field);

					if (field) {
						if (field.dataType === FieldDataType.Numeric) {
							const cell = `${row[index] ?? ""}`;
							const values = cell.split("\n");

							return values?.map((v) => this._appState.actions.formatValue(v, fieldData.field) as string).join("\n");
						}
						let value = (this._appState.actions.formatValue(row[index], fieldData.field) as string) || row[index];

						if (field.dataType === FieldDataType.Boolean) {
							value = row[index];
						}

						return value;
					} else {
						return "";
					}
				}
				return null;
			})
			.filter((field) => field !== null);
	};

	private readonly onEditClick = () => {
		const {report, onEditClick} = this.props;
		onEditClick(report);
		report.startEditing();
	};

	private readonly onDeleteClick = async () => {
		const {report, appState, onClose, canDeleteReports} = this.props;

		if (canDeleteReports([report])) {
			if (report.isGlobal) {
				this.setState({
					viewAction: "delete",
					isGlobalActionPanelOpen: true,
				});
			} else {
				const confirmed = await PopupUtilsV5.getDeleteConfirmationPopupV5(XyiconFeature.Report, 1);

				if (confirmed) {
					const isFavorite = report?.isFavorite;
					await appState.app.transport.services.view.onModifyActiveView([report.id], "deleted");
					await report.delete([report]);

					if (isFavorite) {
						if (appState.user) {
							await appState.user.setFavoriteViews(appState.user.favoriteViews);
						}
					}

					if (onClose) {
						onClose();
					}
				}
			}
		} else {
			await WarningWindowV5.open(`Sorry, you don't have permission to delete this report.`);
		}
	};

	private readonly onDuplicateClick = () => {
		const {report, onDuplicateClick} = this.props;

		onDuplicateClick([report]);
	};

	private readonly onPromoteDemoteClick = () => {
		const {report} = this.props;

		this.setState({
			isGlobalActionPanelOpen: true,
			viewAction: report.isGlobal ? "makePrivate" : "makeGlobal",
		});
	};

	private readonly onSaveAsClick = async () => {
		const {report, transport, onClose, appState} = this.props;
		const reportName = report.name;

		let placeholderName = reportName;

		// get the right duplicate name first
		do {
			placeholderName += " (Duplicate)";
		} while (!this._appState.actions.isNameValidForViewOrReport(placeholderName, XyiconFeature.Report, report.id, true));

		const config: IPopupWindowConfig = {ok: "Save", disableButtonOnEmptyInput: true};

		let name = await PromptWindowV5.open(
			"Please enter the new name you want this to be saved with",
			`Save ${reportName} As`,
			placeholderName,
			"",
			config,
		);

		if (name !== null) {
			name = name || reportName;
			const newReport = report.duplicate(name);
			const duplicatedReports = await transport.services.feature.create<Report>(newReport.serializeData(), XyiconFeature.Report);

			if (duplicatedReports[0] && duplicatedReports[0].scope === ReportScope.CurrentPortfolio) {
				if (!appState.actions.getReportViewById(duplicatedReports[0].id)) {
					appState.reportViews.push(duplicatedReports[0]);
				}
				appState.user?.updateViewFolderStructures();
			}

			if (onClose) {
				onClose();
			}
		}
	};

	private readonly onExportClick = (format: ExportFormat) => {
		const {report} = this.props;
		let data = report.reportData;

		if (this._table.current && data.length > 2) {
			const headers = data[0];

			data = [headers, ...this._table.current.sortData(data.slice(1))];
		}
		report.exportReport(format, data);
	};

	private readonly toggleDetailsPopup = () => {
		this.setState((prev) => ({
			isDetailsPopupOpen: !prev.isDetailsPopupOpen,
		}));
	};

	public override componentDidUpdate(prevProps: IRunReportPageProps, prevState: IRunReportPageState) {
		if (!ObjectUtils.compare(this.props.report, prevProps.report)) {
			this._report = this.props.report;

			if (prevState.search !== "") {
				this.setState({search: ""});
			}
		}

		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	private readonly onSearchInput = (value: string) => {
		this.setState({search: value});
	};

	public override render() {
		const {report, onClose, appState, isNavbarDocked, onRunReport, lastRunTime} = this.props;
		const {search, isDetailsPopupOpen, isArchivePopupOpen, isSharePopupOpen, isGlobalActionPanelOpen, viewAction} = this.state;
		const data = report.reportData?.slice(1) || [];
		const isReportView = report.scope === ReportScope.CurrentPortfolio && report.isUserDefined;
		const hasBeenAnHour = Date.now() - lastRunTime > 60 * 60 * 1000;
		const meatBallMenuOptions: IDropdownOption[] = [];

		if (Report.getPermission(report, appState) === Permission.Delete) {
			meatBallMenuOptions.push({label: "Delete", onClick: this.onDeleteClick});
		}

		if (appState.user.isAdmin) {
			meatBallMenuOptions.push({
				label: "Duplicate",
				onClick: this.onDuplicateClick,
			});
		}

		if (Report.getPermission(report, appState) > Permission.View) {
			meatBallMenuOptions.push({
				label: report.isGlobal ? "Demote to My Report" : "Promote to Global Report",
				onClick: this.onPromoteDemoteClick,
			});
		}

		return (
			<>
				<RunReportStyled
					$isReportView={isReportView}
					className="runreport"
					style={this.props.style || {}}
				>
					<div className="header">
						<div className="headerRow">
							<div className="headerLeft">
								{!isReportView && (
									<IconButtonV5
										IconComponent={BackIcon}
										onClick={onClose}
										title="To Reports"
									/>
								)}
								<RunReportPageHeaderNameStyled>{report.name}</RunReportPageHeaderNameStyled>
								<IconButtonV5
									IconComponent={InfoIcon}
									onClick={this.toggleDetailsPopup}
									title="Details"
								/>
								{report.description && (
									<div
										className="reportDescription"
										onClick={this.toggleDetailsPopup}
									>
										{report.description}
									</div>
								)}
							</div>
							<div className="headerRight">
								<IconButtonV5
									IconComponent={ArchiveIcon}
									label="Report Archive"
									onClick={() => this.setState((prev) => ({isArchivePopupOpen: !prev.isArchivePopupOpen}))}
									title="Report Archive"
									divRef={this._archiveReportButtonRef}
									className="archive-button"
								/>
								{Report.getPermission(report, appState) > Permission.View && (
									<ButtonV5
										className="secondary"
										type="secondary"
										label="Share"
										title="Share"
										onClick={() => this.setState((prev) => ({isSharePopupOpen: !prev.isSharePopupOpen}))}
									>
										<ShareIcon />
									</ButtonV5>
								)}
								{appState.user.isAdmin && (
									<DropdownButtonV5
										button={
											<IconButtonV5
												IconComponent={DotsHorizontalIcon}
												onClick={Functions.emptyFunction}
											/>
										}
										options={meatBallMenuOptions}
										horizontalAlignment={HorizontalAlignment.left}
									/>
								)}
							</div>
						</div>
						<div className="headerRow">
							<div className="headerLeft">
								<SearchFieldV5
									value={search}
									onInput={this.onSearchInput}
									minimized={true}
								/>
								<IconButtonV5
									IconComponent={PlayIcon}
									onClick={() => onRunReport(report)}
									label="Run"
									className="runReportButton"
								/>
								{Report.getPermission(report, appState) > Permission.View && (
									<IconButtonV5
										IconComponent={PenWithLineIcon}
										onClick={this.onEditClick}
										label="Edit"
										className="editReportButton"
									/>
								)}
								<DropdownButtonV5
									button={
										<DropDownTitleStyle>
											<DonwloadIcon className="downloadIcon" />
											Download <ChevronDownIcon />
										</DropDownTitleStyle>
									}
									options={[
										{label: "Download as Excel", onClick: () => this.onExportClick("excel")},
										{label: "Download as CSV", onClick: () => this.onExportClick("csv")},
									]}
								/>
								{appState.user.isAdmin && (
									<ButtonV5
										label="Save As"
										onClick={this.onSaveAsClick}
									/>
								)}
							</div>
							{isReportView && (
								<div className="headerRight">
									<div className={`lastReportRun ${hasBeenAnHour ? "stale" : ""}`}>
										Last Run at: {DateFormatter.format(new Date(lastRunTime).toString())}
									</div>
								</div>
							)}
						</div>
					</div>
					<RunReportTableStyled>
						<SimpleTableV5
							key={`${report.id}_${data.length > 0}`}
							ref={this._table}
							data={data}
							feature={XyiconFeature.Report}
							headers={this.getHeaders()}
							defaultSort={this.getDefaultSort()}
							defaultHeaderWidths={report.tableSettings?.columnWidths}
							onTableSettingsChange={this.onTableSettingsChange}
							getFields={this.getFields}
							tableSearchQuery={search}
							checkboxColumn={false}
							loading={!report.reportLoaded}
							errorMessage={report.errorMessage}
							isCellContentsWrappingOn={true}
							isItemUnfocused={true}
						/>
					</RunReportTableStyled>
					{isDetailsPopupOpen && (
						<RunReportDetailsV5
							report={report}
							onClose={() => this.setState((prev) => ({isDetailsPopupOpen: !prev.isDetailsPopupOpen}))}
						/>
					)}
					{isArchivePopupOpen && (
						<PopupV5
							onClose={() => this.setState((prev) => ({isArchivePopupOpen: !prev.isArchivePopupOpen}))}
							label="Report Archive"
							className="ReportArchive"
							isSmallPopup={true}
							noButtons={true}
							width="400px"
							height="576px"
							parentRef={this._archiveReportButtonRef.current}
							verticalAlignment={VerticalAlignment.bottomOuter}
							horizontalAlignment={HorizontalAlignment.right}
							offsetX={0}
							offsetY={8}
						>
							<ReportPopupStyled>
								<ReportHistoryV5 report={report} />
							</ReportPopupStyled>
						</PopupV5>
					)}
				</RunReportStyled>
				{isSharePopupOpen && (
					<DomPortal destination={appState.app.modalContainer}>
						<ReportSharingContainer
							report={report}
							onClose={() => this.setState((prev) => ({isSharePopupOpen: !prev.isSharePopupOpen}))}
						/>
					</DomPortal>
				)}
				{isGlobalActionPanelOpen && (
					<GlobalSharingPopup
						viewItems={[report]}
						onClose={() => this.setState(() => ({isGlobalActionPanelOpen: false}))}
						viewActionType={viewAction}
						isReports={true}
					/>
				)}
			</>
		);
	}
}
